import MetallicPillButton from '@/components/Base/MetallicPillButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyBrownPanel, SurveyBrownPanelInset } from '@/components/layout/SurveyBrownPanel';
import { fetchOutletsByAddressQuery, type OutletSearchRow } from '@/api/outlet';
import { isQ7_1Complete } from '@/lib/q7_1Storage';
import { isQ7_2Complete } from '@/lib/q7_2Storage';
import { isQ7_3Complete, readQ7_3Answers } from '@/lib/q7_3Storage';
import { writeQ7_4Answers } from '@/lib/q7_4Storage';
import { isSmokerProfileComplete } from '@/lib/smokerProfileStorage';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { useAppSelector } from '@/stores/hooks';
import { selectPdpaConsent, selectSurveyScreening, selectUniqueCodeDraft } from '@/stores/selectors';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

const textareaClass =
  'w-full resize-none rounded border border-slate-300/90 bg-white px-3 py-2 text-left text-[15px] font-medium leading-snug text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300 sm:text-[16px]';

const checkboxLabelClass =
  'flex cursor-pointer items-start gap-3 text-left text-[15px] font-medium leading-snug text-white/95 sm:text-[16px]';

const checkboxClass =
  'mt-0.5 h-[18px] w-[18px] shrink-0 rounded border border-slate-300/90 bg-white text-amber-800 focus:ring-1 focus:ring-slate-300';

/** Saved as Q7.4 answer when the customer ticks “can’t find my location”. */
const Q7_4_CANT_FIND_ANSWER = "Sorry, I can't find my location";

function formatOutletInTextarea(o: OutletSearchRow): string {
  const name = o.outlet_name?.trim() || 'Outlet';
  const addr = o.address?.trim() ?? '';
  return addr ? `${name}\n${addr}` : name;
}

export default function Q7_4() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { mobileLocalDigits, customerUuid } = useAppSelector(selectUniqueCodeDraft);

  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [results, setResults] = useState<OutletSearchRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedOutlet, setSelectedOutlet] = useState<OutletSearchRow | null>(null);
  const [cantFindLocation, setCantFindLocation] = useState(false);

  /** Short debounce so results feel instant while typing without firing every key. */
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query.trim()), 90);
    return () => window.clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (selectedOutlet || cantFindLocation) {
      setLoading(false);
      return;
    }

    if (debounced.length < 1) {
      setResults([]);
      setLoading(false);
      setSearchError(null);
      return;
    }

    let cancelled = false;
    const ac = new AbortController();
    setLoading(true);
    setSearchError(null);

    fetchOutletsByAddressQuery(debounced, ac.signal)
      .then((rows) => {
        if (cancelled) return;
        setResults(rows);
      })
      .catch(() => {
        if (cancelled) return;
        setResults([]);
        setSearchError('Could not search outlets. Please try again.');
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [debounced, selectedOutlet, cantFindLocation]);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    if (selectedOutlet && value !== formatOutletInTextarea(selectedOutlet)) {
      setSelectedOutlet(null);
    }
  };

  const pickOutlet = (o: OutletSearchRow) => {
    setCantFindLocation(false);
    setSelectedOutlet(o);
    setQuery(formatOutletInTextarea(o));
    setResults([]);
    setSearchError(null);
    setLoading(false);
  };

  const handleCantFindChange = (checked: boolean) => {
    setCantFindLocation(checked);
    if (checked) {
      setSelectedOutlet(null);
      setResults([]);
      setSearchError(null);
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (cantFindLocation) {
      writeQ7_4Answers({
        answer: Q7_4_CANT_FIND_ANSWER,
        cantFindLocation: true,
        outletUuid: null,
        address: query.trim(),
        outletName: null,
      });
      navigate('/devreg');
      return;
    }
    if (!selectedOutlet) return;
    const address = selectedOutlet.address?.trim() ?? '';
    const textAnswer = query.trim();
    writeQ7_4Answers({
      answer: textAnswer,
      cantFindLocation: false,
      outletUuid: selectedOutlet.uuid,
      address,
      outletName: selectedOutlet.outlet_name,
    });
    navigate('/devreg');
  };

  const canSubmit = cantFindLocation || selectedOutlet != null;

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }
  if (!pdpaConsent) {
    return <Navigate to="/pdpa" replace />;
  }
  if (!mobileLocalDigits || mobileLocalDigits.replace(/\D/g, '').length === 0) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!customerUuid) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!isSmokerProfileComplete()) {
    return <Navigate to="/smokerprofile" replace />;
  }
  if (!isQ7_1Complete()) {
    return <Navigate to="/7_1" replace />;
  }
  if (!isQ7_2Complete()) {
    return <Navigate to="/7_2" replace />;
  }
  if (!isQ7_3Complete()) {
    return <Navigate to="/7_3" replace />;
  }
  if (readQ7_3Answers()?.oos !== 'Yes') {
    return <Navigate to="/devreg" replace />;
  }

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex min-h-0 flex-1 flex-col justify-center overflow-hidden">
        <div className="mx-auto flex w-full max-w-full flex-col py-6">
          <SurveyBrownPanelInset>
            <SurveyBrownPanel className="!p-4">
              <p className="m-0 text-left text-[16px] font-medium leading-snug text-white/95 sm:text-[17px]">
                {t.q7_4.question}
              </p>
              <div className="mt-5">
                <textarea
                  value={query}
                  onChange={(e) => handleQueryChange(e.target.value)}
                  rows={3}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder={t.q7_4.placeholder}
                  className={textareaClass}
                  aria-label="Store location search"
                  aria-controls="q74-outlet-results"
                  aria-expanded={!selectedOutlet && !cantFindLocation && debounced.length >= 1}
                />
                {!selectedOutlet && !cantFindLocation && debounced.length >= 1 && (
                  <div
                    id="q74-outlet-results"
                    className="mt-2 max-h-[min(70vh,32rem)] overflow-y-auto rounded border border-white/25 bg-black/25"
                    role="listbox"
                    aria-label="Matching outlets"
                  >
                    {loading && (
                      <p className="m-0 px-3 py-2 text-left text-[14px] text-white/80 sm:text-[15px]">
                        {t.q7_4.searching}
                      </p>
                    )}
                    {!loading && searchError && (
                      <p className="m-0 px-3 py-2 text-left text-[14px] text-amber-200/95 sm:text-[15px]">
                        {t.q7_4.searchError}
                      </p>
                    )}
                    {!loading &&
                      !searchError &&
                      results.length === 0 &&
                      debounced.length >= 1 && (
                        <p className="m-0 px-3 py-2 text-left text-[14px] text-white/80 sm:text-[15px]">
                          {t.q7_4.noResults}
                        </p>
                      )}
                    {!loading &&
                      !searchError &&
                      results.map((o) => (
                        <button
                          key={o.uuid}
                          type="button"
                          role="option"
                          className="w-full border-b border-white/10 px-3 py-2.5 text-left text-[14px] font-medium leading-snug text-white/95 last:border-b-0 hover:bg-white/10 sm:text-[15px]"
                          onClick={() => pickOutlet(o)}
                        >
                          <span className="block">{o.outlet_name?.trim() || 'Outlet'}</span>
                          {o.address?.trim() ? (
                            <span className="mt-0.5 block text-[13px] font-normal text-white/85 sm:text-[14px]">
                              {o.address.trim()}
                            </span>
                          ) : null}
                        </button>
                      ))}
                  </div>
                )}
              </div>
              <div className="mt-5">
                <label htmlFor="q74-cant-find" className={checkboxLabelClass}>
                  <input
                    id="q74-cant-find"
                    type="checkbox"
                    checked={cantFindLocation}
                    onChange={(e) => handleCantFindChange(e.target.checked)}
                    className={checkboxClass}
                  />
                  <span>{t.q7_4.cantFind}</span>
                </label>
              </div>
            </SurveyBrownPanel>
          </SurveyBrownPanelInset>
          <SurveyBrownPanelInset className="mt-8">
            <div className="flex justify-center">
              <MetallicPillButton
                type="button"
                size="sm"
                className="metallic-pill--lang-home"
                disabled={!canSubmit}
                onClick={handleSubmit}
              >
                {t.q7_4.submit}
              </MetallicPillButton>
            </div>
          </SurveyBrownPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
